/**
 * AmenityRanker.js
 *
 * Left panel : searchable checklist of all amenities.
 * Right panel: drag-to-reorder ranked list.
 *   - boolean   → on/off toggle  (داشته باشد)
 *   - numerical → number input
 *   - categorical → select from categorical_options
 *
 * Each item also has a "required" toggle (hard filter for matching).
 *
 * Props:
 *   allAmenities  — [{ id, name, description, value_type, categorical_options, min_value, max_value }]
 *   selected      — [{ amenity, amenity_label, value_type, rank, required, value }]
 *   onChange(selected) — called on any change
 */

import { useState } from 'react';
import { parseRangeDefinition } from './HardAmenityInput';

/* ── value input rendered inside the ranked row ── */
function ValueInput({ item, amenityMeta, onChange }) {
    const vt = amenityMeta?.value_type || item.value_type;

    if (vt === 'boolean') {
        const on = item.value === true;
        return (
            <button
                type="button"
                className={`ar-toggle ${on ? 'ar-toggle-on' : ''}`}
                onClick={() => onChange(!on)}
                title={on ? 'دارد (کلیک برای تغییر)' : 'ندارد (کلیک برای تغییر)'}
                aria-pressed={on}
            >
                <span className="ar-toggle-knob" />
                <span className="ar-toggle-label">{on ? 'دارد' : 'ندارد'}</span>
            </button>
        );
    }

    if (vt === 'numerical') {
        // Use range buckets if available, otherwise fall back to plain number input
        const buckets = parseRangeDefinition(amenityMeta?.hcm_range_definition);
        if (buckets.length) {
            const allKeys = buckets.map(b => b.key);
            const isAll = !item.value || !Array.isArray(item.value) || item.value.length === allKeys.length;
            const selSet = new Set((Array.isArray(item.value) ? item.value : []).map(String));
            const toggle = (key) => {
                const k = String(key);
                let next = selSet.has(k)
                    ? [...selSet].filter(x => x !== k).map(Number)
                    : [...selSet, k].map(Number);
                if (!next.length || next.length === allKeys.length) next = allKeys;
                onChange(next);
            };
            return (
                <div className="hai-chips ar-chips">
                    <button
                        type="button"
                        className={`hai-chip hai-chip-any ${isAll ? 'hai-chip-on' : ''}`}
                        onClick={e => { e.stopPropagation(); onChange(allKeys); }}
                    >مهم نیست</button>
                    {buckets.map(b => (
                        <button
                            key={b.key}
                            type="button"
                            className={`hai-chip ${!isAll && selSet.has(String(b.key)) ? 'hai-chip-on' : ''}`}
                            onClick={e => { e.stopPropagation(); toggle(b.key); }}
                        >{b.label}</button>
                    ))}
                </div>
            );
        }
        return (
            <input
                type="number"
                className="ar-val-input"
                placeholder="مقدار"
                value={item.value ?? ''}
                min={amenityMeta?.min_value ?? undefined}
                max={amenityMeta?.max_value ?? undefined}
                onClick={e => e.stopPropagation()}
                onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
            />
        );
    }

    if (vt === 'categorical') {
        const opts = amenityMeta?.categorical_options || {};
        const options = Object.entries(opts).map(([k, label]) => ({
            key: isNaN(Number(k)) ? k : Number(k),
            label,
        }));
        if (!options.length) return null;
        const allKeys = options.map(o => o.key);
        const isAll = !item.value || !Array.isArray(item.value) || item.value.length === allKeys.length;
        const selSet = new Set((Array.isArray(item.value) ? item.value : []).map(String));
        const toggle = (key) => {
            const k = String(key);
            let next = selSet.has(k)
                ? [...selSet].filter(x => x !== k).map(v => isNaN(Number(v)) ? v : Number(v))
                : [...selSet, k].map(v => isNaN(Number(v)) ? v : Number(v));
            if (!next.length || next.length === allKeys.length) next = allKeys;
            onChange(next);
        };
        return (
            <div className="hai-chips ar-chips">
                <button
                    type="button"
                    className={`hai-chip hai-chip-any ${isAll ? 'hai-chip-on' : ''}`}
                    onClick={e => { e.stopPropagation(); onChange(allKeys); }}
                >مهم نیست</button>
                {options.map(opt => (
                    <button
                        key={opt.key}
                        type="button"
                        className={`hai-chip ${!isAll && selSet.has(String(opt.key)) ? 'hai-chip-on' : ''}`}
                        onClick={e => { e.stopPropagation(); toggle(opt.key); }}
                    >{opt.label}</button>
                ))}
            </div>
        );
    }

    return null;
}

export default function AmenityRanker({ allAmenities = [], selected = [], onChange, forceRequired = false }) {
    const [search, setSearch] = useState('');

    // keyed lookup for meta (value_type, options, etc.)
    const metaById = Object.fromEntries(allAmenities.map(a => [a.id, a]));

    const selectedIds = new Set(selected.map(s => s.amenity));

    const filtered = allAmenities.filter(a =>
        (a.description || a.name).toLowerCase().includes(search.toLowerCase())
    );

    /* ── add / remove ── */
    const toggle = (amenity) => {
        if (selectedIds.has(amenity.id)) {
            const next = selected
                .filter(s => s.amenity !== amenity.id)
                .map((s, i) => ({ ...s, rank: i + 1 }));
            onChange(next);
        } else {
            // default value per type
            const defaultValue = amenity.value_type === 'boolean' ? true : null;
            onChange([...selected, {
                amenity: amenity.id,
                amenity_label: amenity.description || amenity.name,
                value_type: amenity.value_type,
                rank: selected.length + 1,
                required: forceRequired ? true : false,
                value: defaultValue,
            }]);
        }
    };

    /* ── update a single field on a ranked item ── */
    const updateItem = (amenityId, patch) => {
        onChange(selected.map(s => s.amenity === amenityId ? { ...s, ...patch } : s));
    };

    /* ── move up / down ── */
    const moveItem = (idx, dir) => {
        const next = [...selected];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return;
        [next[idx], next[target]] = [next[target], next[idx]];
        onChange(next.map((s, i) => ({ ...s, rank: i + 1 })));
    };

    return (
        <div className="ar-wrap">
            {/* ── Left: picker ── */}
            <div className="ar-picker">
                <input
                    className="prw-input ar-search"
                    placeholder="جستجوی امکانات..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <ul className="ar-list">
                    {filtered.map(a => (
                        <li
                            key={a.id}
                            className={`ar-item ${selectedIds.has(a.id) ? 'ar-item-checked' : ''}`}
                            onClick={() => toggle(a)}
                        >
                            <span className="ar-check">{selectedIds.has(a.id) ? '✓' : ''}</span>
                            <span className="ar-item-name">{a.description || a.name}</span>
                            {a.value_type && a.value_type !== 'boolean' && (
                                <span className="ar-item-type">{a.value_type}</span>
                            )}
                        </li>
                    ))}
                    {filtered.length === 0 && (
                        <li className="ar-empty">موردی یافت نشد</li>
                    )}
                </ul>
            </div>

            {/* ── Right: ranked list ── */}
            <div className="ar-ranked">
                <p className="ar-ranked-title">
                    {selected.length ? 'ترتیب اهمیت را مشخص کنید' : 'امکانات انتخابی اینجا نمایش داده می‌شوند'}
                </p>
                <ul className="ar-rank-list">
                    {selected.map((s, idx) => (
                        <li key={s.amenity} className="ar-rank-item">
                            <span className="ar-rank-num">{s.rank}</span>

                            <div className="s5g-move-btns">
                                <button
                                    type="button"
                                    className="s5g-move-btn"
                                    onClick={() => moveItem(idx, -1)}
                                    disabled={idx === 0}
                                    aria-label="اولویت بالاتر"
                                >▲</button>
                                <button
                                    type="button"
                                    className="s5g-move-btn"
                                    onClick={() => moveItem(idx, 1)}
                                    disabled={idx === selected.length - 1}
                                    aria-label="اولویت پایین‌تر"
                                >▼</button>
                            </div>

                            <span className="ar-rank-label">{s.amenity_label}</span>

                            <ValueInput
                                item={s}
                                amenityMeta={metaById[s.amenity]}
                                onChange={(val) => updateItem(s.amenity, { value: val })}
                            />

                            <button
                                type="button"
                                className={`ar-req-btn ${s.required ? 'ar-req-on' : ''}`}
                                onClick={() => !forceRequired && updateItem(s.amenity, { required: !s.required })}
                                title={forceRequired ? 'الزامی (ثابت)' : s.required ? 'الزامی' : 'اختیاری'}
                                disabled={forceRequired}
                            >
                                {forceRequired ? 'الزامی ✓' : s.required ? 'الزامی' : 'اختیاری'}
                            </button>
                            <button
                                type="button"
                                className="ar-remove-btn"
                                onClick={() => toggle({ id: s.amenity })}
                                aria-label="حذف"
                            >✕</button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
